// Editor ÚNICO de recetas — lo usan las dos entradas del módulo:
//   · Centro Médico → Clínica → Recetas (modal sobre el listado)
//   · Ficha del paciente → Recetas (inline, estilo reliv)
// Reúne el formato reliv (CIE estructurado, alergias precargadas de los
// antecedentes, signos de alarma, plantillas, Lista Medicamentos/Vademécum)
// con lo clínico-administrativo del módulo (crónica/renovable con refills,
// validez en días y firma del paciente en canvas). Guarda vía med_crear_receta.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, Modal, Switch, fieldCls, med, medRpc } from './shared';
import type { Diagnostico, Plantilla } from './shared';
import { DiagnosticosCie } from './DiagnosticosCie';
import { useVoiceForm, type CampoVoz } from '@/contexts/VoiceFormContext';

const lbl = 'text-xs font-semibold uppercase text-muted-foreground';

export const VIAS_ADMIN = ['Inhalador', 'Intraarterial', 'Intraarticular', 'Intramuscular', 'Intranasal', 'Intraoseo', 'Intraotico',
  'Intratecal', 'Intravenoso', 'Ocular', 'Oral', 'Rectal', 'Subcutánea', 'Sublingual', 'Tópico', 'Transdérmico', 'Vaginal'];
export const FORMAS_FARM = ['Aceite', 'Acondicionador', 'Agujas', 'Ampolla(s)', 'Anillo', 'Balsamo Labial', 'Barra', 'Bebida(s)', 'Caja(s)',
  'Cápsula(s)', 'Cepillo', 'Comprimidos', 'Crema', 'DIU', 'Emulsión', 'Enjuague bucal', 'Espuma', 'Frasco(s)', 'Gel', 'Gomita(s)',
  'Gotas', 'Gotero', 'Grageas', 'Inhalador', 'Jabón', 'Jalea', 'Jarabe', 'Jeringuilla', 'Jeringuilla precargada', 'Laca', 'Loción',
  'Microenema', 'Óvulo(s)', 'Parche(s)', 'Pen (esfero)', 'Polvo', 'Pomada', 'Pomo', 'Sachet', 'Serum', 'Shampoo', 'Sobre(s)',
  'Solución', 'Spray', 'Supositorio(s)', 'Suspensión', 'Tableta masticable', 'Tableta(s)', 'Tarro(s)', 'Tubo', 'Ungüento', 'Vial'];
export const SIGNOS_ALARMA = ['Cefalea', 'Confusion', 'Constipación', 'Diarrea', 'DISNEA', 'Dolor abdominal', 'DOLOR INTENSO',
  'DOLOR OCULAR INTENSO', 'DOLOR TORACICO', 'EDEMA PALPEBRAL QUE IMPIDE ABRIR EL OJO.', 'Fatiga', 'FIEBRE',
  'FOTOFOBIA MARCADA (MOLESTIA EXTREMA A LA LUZ)', 'Hipersensibilidad', 'Mareo', 'Nausea',
  'PERDIDA SUBITA DE LA AGUDEZA VISUAL', 'Rush', 'SANGRADO DE ENCIAS', 'TAQUICARDIA', 'Vomito'];

export interface ItemReliv {
  producto_id: string | null; medicamento: string; comercial: string; concentracion: string;
  via: string; forma: string; cantidad: string; indicaciones: string;
}
const itemRelivVacio: ItemReliv = { producto_id: null, medicamento: '', comercial: '', concentracion: '', via: '', forma: '', cantidad: '', indicaciones: '' };

interface MedOpt {
  id: string; codigo: string | null; nombre: string; principio_activo: string | null; concentracion: string | null; presentacion: string | null;
  marca: string | null; requiere_receta: boolean; forma: string | null; via: string | null; stock: number;
}
interface FavOpt { id: string; medicamento: string; concentracion: string | null; forma: string | null; dosis: string | null; indicaciones: string | null }

// Campos de cabecera que el dictado por voz puede llenar (Fase 2)
const CAMPOS_VOZ_RECETA: CampoVoz[] = [
  { clave: 'diagnostico', etiqueta: 'Diagnóstico (texto libre)', tipo: 'textarea' },
  { clave: 'recomendaciones', etiqueta: 'Recomendaciones no farmacológicas', tipo: 'textarea' },
  { clave: 'alergias', etiqueta: 'Alergias farmacológicas', tipo: 'texto' },
  { clave: 'ciudad', etiqueta: 'Ciudad', tipo: 'texto' },
  { clave: 'proxima_cita', etiqueta: 'Próxima cita', tipo: 'fecha' },
];

// Plantillas por tipo de documento (Utilizar / Guardar plantilla). Vive aquí
// (y no en DocumentosFicha) para que ambos archivos puedan importarla sin ciclos.
export function PlantillasDoc({ categoria, getDatos, onAplicar }: {
  categoria: string; getDatos: () => Record<string, unknown>; onAplicar: (c: Record<string, unknown>) => void;
}) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: plantillas = [] } = useQuery<Plantilla[]>({
    queryKey: ['med-plantillas-doc', categoria],
    queryFn: () => med.list<Plantilla>('plantilla', `?select=id,nombre,contenido&deleted_at=is.null&es_activo=is.true&categoria=eq.${categoria}&order=nombre`),
  });
  const guardar = useMutation({
    mutationFn: (nombrePl: string) => {
      const existente = plantillas.find((p) => p.nombre === nombrePl.trim());
      return medRpc('med_plantilla_guardar', { _data: { id: existente?.id, nombre: nombrePl.trim(), categoria, contenido: getDatos() } });
    },
    onSuccess: () => { toast.success('Plantilla guardada'); void qc.invalidateQueries({ queryKey: ['med-plantillas-doc', categoria] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirNombre = async () => {
    const v = await dlg.pedir('Guardar plantilla', [{ key: 'nombre', label: 'Nombre de la plantilla' }]);
    if (v === null) return;
    guardar.mutate(v.nombre);
  };
  return (
    <div className="flex items-center gap-2">
      {dlg.ui}
      <select className={`${fieldCls} w-auto min-w-[200px]`} value=""
        onChange={(e) => { const p = plantillas.find((x) => x.id === e.target.value); if (p) onAplicar(p.contenido as Record<string, unknown>); }}>
        <option value="">Utilizar una plantilla…</option>
        {plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
      </select>
      <Button size="sm" variant="outline" isLoading={guardar.isPending} onClick={() => void pedirNombre()}>💾 Guardar plantilla</Button>
    </div>
  );
}

interface FormReceta {
  ciudad: string; proxima_cita: string; alergias: string; diagnostico: string;
  dx: Diagnostico[]; signos: string[]; items: ItemReliv[]; recomendaciones: string;
  cronica: boolean; refills: number; validez: number;
}

export function RecetaEditor({ pacienteId, diagnosticoInicial, dxInicial, onCancel, onSaved }: {
  pacienteId: string; diagnosticoInicial?: string; dxInicial?: Diagnostico[]; onCancel: () => void; onSaved: () => void;
}) {
  const qc = useQueryClient();
  // El médico firmante es el usuario logueado (no se elige).
  const { data: miMedico } = useQuery<{ id: string; razon_social: string; numero_licencia: string | null } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string; numero_licencia: string | null } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  // prefill de alergias farmacológicas desde los antecedentes personales
  const { data: ant } = useQuery<{ personal: { datos: Record<string, unknown> } | null }>({
    queryKey: ['med-ficha', pacienteId, 'antecedentes'],
    queryFn: () => medRpc('med_antecedentes', { _paciente_id: pacienteId }),
  });

  const [form, setForm] = useState<FormReceta>({
    ciudad: '', proxima_cita: '', alergias: '', diagnostico: diagnosticoInicial ?? '',
    dx: dxInicial ?? [], signos: [], items: [{ ...itemRelivVacio }], recomendaciones: '',
    cronica: false, refills: 3, validez: 3,
  });
  const [alergiasTocadas, setAlergiasTocadas] = useState(false);
  // Herencia de CIE: si no vino una lista explícita (dxInicial) y el médico no ha tocado la
  // sección, precarga los diagnósticos de la ÚLTIMA consulta del paciente. Reusa la misma
  // query de la historia (mismo cache que la ficha, no dispara una consulta extra).
  const [dxTocado, setDxTocado] = useState(false);
  const { data: histParaDx } = useQuery<{ evoluciones?: Array<{ evolucion: { episodio_id?: string | null; fecha: string }; diagnosticos?: Diagnostico[] }> }>({
    queryKey: ['med-ficha', pacienteId, 'historia'],
    queryFn: () => medRpc('med_historia', { _paciente_id: pacienteId }),
    enabled: !(dxInicial && dxInicial.length) && !!pacienteId,
  });
  useEffect(() => {
    if (dxTocado || (dxInicial && dxInicial.length) || form.dx.length) return;
    const raices = (histParaDx?.evoluciones ?? []).filter((e) => !e.evolucion.episodio_id);
    const ultima = [...raices].sort((a, b) => (a.evolucion.fecha < b.evolucion.fecha ? 1 : -1))[0];
    if (ultima?.diagnosticos?.length) setForm((f) => ({ ...f, dx: ultima.diagnosticos! }));
  }, [histParaDx, dxTocado, dxInicial, form.dx.length]);
  const [modal, setModal] = useState<'medicamentos' | 'vademecum' | null>(null);
  const [busqMed, setBusqMed] = useState('');
  // Vademécum: un filtro por columna (se combinan; sin importar tildes ni mayúsculas)
  const filtroFavVacio = { medicamento: '', concentracion: '', forma: '', indicaciones: '' };
  const [filtroFav, setFiltroFav] = useState(filtroFavVacio);
  // «Lista Medicamentos»: búsqueda en el servidor (965) por genérico, comercial, concentración, código o marca
  const [busqDeb, setBusqDeb] = useState('');
  useEffect(() => { const t = setTimeout(() => setBusqDeb(busqMed.trim()), 250); return () => clearTimeout(t); }, [busqMed]);
  const { data: buscados, isFetching: buscando } = useQuery<{ total: number; limite: number; items: MedOpt[] }>({
    queryKey: ['med-medicamentos', busqDeb],
    queryFn: () => medRpc('medicamentos_buscar', { _q: busqDeb || null, _limite: 60 }),
    enabled: modal === 'medicamentos', staleTime: 60_000,
  });

  const alergiasPrefill = (() => {
    const d = ant?.personal?.datos;
    return d && typeof d.alergias_farmacologicas === 'string' ? (d.alergias_farmacologicas as string) : '';
  })();
  const alergias = alergiasTocadas || form.alergias ? form.alergias : alergiasPrefill;

  const { data: favoritos = [] } = useQuery<FavOpt[]>({
    queryKey: ['med-favoritos', miMedicoId],
    queryFn: () => medRpc<FavOpt[]>('med_favoritos', { _medico_id: miMedicoId }),
    enabled: !!miMedicoId && modal === 'vademecum',
  });

  const setItem = (i: number, patch: Partial<ItemReliv>) =>
    setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  // ---- Asistente de voz (Fase 2): dictado de la receta con match de fármacos ----
  useVoiceForm({
    modulo: 'receta',
    pacienteId,
    campos: CAMPOS_VOZ_RECETA,
    receta: { vias: VIAS_ADMIN, formas: FORMAS_FARM },
    aplicar: (v) => {
      setForm((f) => ({
        ...f,
        diagnostico: typeof v.diagnostico === 'string' ? v.diagnostico : f.diagnostico,
        recomendaciones: typeof v.recomendaciones === 'string' ? v.recomendaciones : f.recomendaciones,
        ciudad: typeof v.ciudad === 'string' ? v.ciudad : f.ciudad,
        proxima_cita: typeof v.proxima_cita === 'string' ? v.proxima_cita : f.proxima_cita,
        alergias: typeof v.alergias === 'string' ? v.alergias : f.alergias,
      }));
      if (typeof v.alergias === 'string') setAlergiasTocadas(true);
    },
    aplicarReceta: (nuevos) => {
      setForm((f) => {
        const filas = nuevos.map((n) => ({ ...itemRelivVacio, ...n } as ItemReliv));
        // reutiliza las filas vacías del grid antes de agregar nuevas
        const conDatos = f.items.filter((it) => it.medicamento.trim());
        return { ...f, items: [...conDatos, ...filas] };
      });
    },
  });

  const guardarFavorito = useMutation({
    mutationFn: (it: ItemReliv) => medRpc('med_favorito_guardar', { _data: { medico_id: miMedicoId, ...it } }),
    onSuccess: () => { toast.success('Guardado en favoritos del médico'); void qc.invalidateQueries({ queryKey: ['med-favoritos', miMedicoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    mutationFn: () => {
      const items = form.items.filter((it) => it.medicamento.trim());
      if (!items.length) return Promise.reject(new Error('Agrega al menos un medicamento'));
      return medRpc('med_crear_receta', {
        _data: {
          paciente_id: pacienteId, medico_id: miMedicoId, diagnostico: form.diagnostico || null,
          indicaciones: form.recomendaciones || null,
          tipo: form.cronica ? 'cronica' : 'normal', renovable: form.cronica,
          refills_total: form.cronica ? form.refills : 0, validez_dias: form.validez,
          items: items.map((it) => ({ ...it, forma: it.forma || null, via: it.via || null })),
          datos: {
            ciudad: form.ciudad, proxima_cita: form.proxima_cita, alergias_farmacologicas: alergias,
            signos_alarma: form.signos, recomendaciones: form.recomendaciones, diagnosticos: form.dx,
          },
        },
      });
    },
    onSuccess: () => { toast.success('Receta emitida'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const elegirMed = (m: MedOpt | FavOpt) => {
    const esCat = 'nombre' in m;
    const nuevo: ItemReliv = {
      ...itemRelivVacio,
      producto_id: esCat ? (m as MedOpt).id : null,
      // del catálogo: genérico = principio activo, comercial = nombre del producto; forma y vía sugeridas
      medicamento: esCat ? ((m as MedOpt).principio_activo || (m as MedOpt).nombre) : (m as FavOpt).medicamento,
      comercial: esCat && (m as MedOpt).principio_activo ? (m as MedOpt).nombre : '',
      concentracion: m.concentracion ?? '',
      via: esCat ? ((m as MedOpt).via ?? '') : '',
      forma: (esCat ? ((m as MedOpt).forma ?? (m as MedOpt).presentacion) : (m as FavOpt).forma) ?? '',
      indicaciones: !esCat ? ((m as FavOpt).indicaciones ?? '') : '',
    };
    setForm((f) => {
      const vacia = f.items.findIndex((it) => !it.medicamento.trim());
      const items = vacia >= 0 ? f.items.map((it, j) => (j === vacia ? nuevo : it)) : [...f.items, nuevo];
      return { ...f, items };
    });
    setModal(null);
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <p className="font-semibold">Receta nueva <span className="font-normal text-muted-foreground">(el número se asigna al guardar)</span></p>
        <PlantillasDoc categoria="receta"
          getDatos={() => ({ ciudad: form.ciudad, alergias, diagnostico: form.diagnostico, signos: form.signos, items: form.items.filter((i) => i.medicamento.trim()), recomendaciones: form.recomendaciones })}
          onAplicar={(c) => { setForm({
            ...form,
            ciudad: typeof c.ciudad === 'string' ? c.ciudad : form.ciudad,
            alergias: typeof c.alergias === 'string' ? c.alergias : alergias,
            diagnostico: typeof c.diagnostico === 'string' ? c.diagnostico : form.diagnostico,
            signos: Array.isArray(c.signos) ? (c.signos as string[]) : form.signos,
            items: Array.isArray(c.items) && c.items.length ? (c.items as ItemReliv[]) : form.items,
            recomendaciones: typeof c.recomendaciones === 'string' ? c.recomendaciones : form.recomendaciones,
          }); setAlergiasTocadas(true); }} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Campo label="Médico firmante">
          <input className={`${fieldCls} bg-muted/50`} value={miMedico ? `${miMedico.razon_social}${miMedico.numero_licencia ? ` · Lic. ${miMedico.numero_licencia}` : ''}` : '—'} readOnly />
        </Campo>
        <Campo label="Ciudad"><input className={fieldCls} placeholder="Ciudad" value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></Campo>
        <Campo label="Próxima cita"><input type="date" className={fieldCls} value={form.proxima_cita} onChange={(e) => setForm({ ...form, proxima_cita: e.target.value })} /></Campo>
        <Campo label="Alergias farmacológicas"><input className={fieldCls} value={alergias} onChange={(e) => { setAlergiasTocadas(true); setForm({ ...form, alergias: e.target.value }); }} /></Campo>
      </div>

      {/* Crónica / renovable + validez (paciente crónico → refills) */}
      <div className="flex flex-wrap items-end gap-4 rounded-md border px-3 py-2">
        <Switch checked={form.cronica} onChange={(v) => setForm({ ...form, cronica: v })} label="Receta crónica / renovable" />
        {form.cronica && (
          <Campo label="Renovaciones (refills)">
            <input type="number" min={1} max={12} className={`${fieldCls} w-24`} value={form.refills}
              onChange={(e) => setForm({ ...form, refills: Math.max(1, Number(e.target.value) || 1) })} />
          </Campo>
        )}
        <Campo label="Validez (días, máx. 3)">
          <input type="number" min={1} max={3} className={`${fieldCls} w-24`} value={form.validez}
            onChange={(e) => setForm({ ...form, validez: Math.min(3, Math.max(1, Number(e.target.value) || 3)) })} />
        </Campo>
      </div>

      <span className={lbl}>Diagnóstico:</span>
      <DiagnosticosCie dx={form.dx} setDx={(dx) => { setDxTocado(true); setForm((f) => ({ ...f, dx })); }} />
      <AutoTextarea placeholder="Diagnóstico (texto libre)…" value={form.diagnostico} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} />
      <div>
        <span className={lbl}>Signos de Alarma</span>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {form.signos.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
              {s}
              <button type="button" title="Quitar" aria-label="Quitar" onClick={() => setForm({ ...form, signos: form.signos.filter((_, j) => j !== i) })}>×</button>
            </span>
          ))}
          <select className={`${fieldCls} w-auto`} value=""
            onChange={(e) => { if (e.target.value && !form.signos.includes(e.target.value)) setForm({ ...form, signos: [...form.signos, e.target.value] }); }}>
            <option value="">+ Agregar signo de alarma…</option>
            {SIGNOS_ALARMA.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Grid de medicamentos estilo reliv */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b bg-muted text-muted-foreground">
            <th className="py-1.5 px-1 text-left">Genérico</th><th className="py-1.5 px-1 text-left">Comercial</th>
            <th className="py-1.5 px-1 text-left">Concentración</th><th className="py-1.5 px-1 text-left">Vía Administración</th>
            <th className="py-1.5 px-1 text-left">Presentación</th><th className="py-1.5 px-1 text-left">Cantidad</th>
            <th className="py-1.5 px-1 text-left">Indicaciones</th><th className="py-1.5 px-1 text-right">Acción</th>
          </tr></thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i} className="border-b last:border-0 align-top">
                <td className="py-1 px-1"><input className={`${fieldCls} min-w-[130px] text-xs`} placeholder="Genérico" value={it.medicamento} onChange={(e) => setItem(i, { medicamento: e.target.value, producto_id: null })} /></td>
                <td className="py-1 px-1"><input className={`${fieldCls} min-w-[110px] text-xs`} placeholder="Comercial" value={it.comercial} onChange={(e) => setItem(i, { comercial: e.target.value })} /></td>
                <td className="py-1 px-1"><input className={`${fieldCls} min-w-[100px] text-xs`} placeholder="Concentración" value={it.concentracion} onChange={(e) => setItem(i, { concentracion: e.target.value })} /></td>
                <td className="py-1 px-1">
                  <select className={`${fieldCls} min-w-[120px] text-xs`} value={it.via} onChange={(e) => setItem(i, { via: e.target.value })}>
                    <option value="">Seleccione...</option>{VIAS_ADMIN.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </td>
                <td className="py-1 px-1">
                  <select className={`${fieldCls} min-w-[120px] text-xs`} value={it.forma} onChange={(e) => setItem(i, { forma: e.target.value })}>
                    <option value="">Seleccione...</option>{FORMAS_FARM.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </td>
                <td className="py-1 px-1"><input className={`${fieldCls} w-20 text-xs`} placeholder="Cantidad" value={it.cantidad} onChange={(e) => setItem(i, { cantidad: e.target.value })} /></td>
                <td className="py-1 px-1"><AutoTextarea className="min-w-[160px] text-xs" value={it.indicaciones} onChange={(e) => setItem(i, { indicaciones: e.target.value })} /></td>
                <td className="py-1 px-1 text-right whitespace-nowrap">
                  <button type="button" className="text-amber-500 disabled:opacity-30 mr-1" title="Guardar como favorito del médico" aria-label="Guardar como favorito del médico"
                    disabled={!miMedicoId || !it.medicamento.trim() || guardarFavorito.isPending} onClick={() => guardarFavorito.mutate(it)}><Star className="w-3.5 h-3.5" /></button>
                  <button type="button" className="text-red-500" title="Quitar" aria-label="Quitar"
                    onClick={() => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, j) => j !== i) : [{ ...itemRelivVacio }] }))}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...itemRelivVacio }] }))}><Plus className="w-3.5 h-3.5 mr-1" /> Fila</Button>
        <Button size="sm" variant="outline" onClick={() => { setBusqMed(''); setModal('medicamentos'); }}>Lista Medicamentos</Button>
        <Button size="sm" variant="outline" disabled={!miMedicoId} title={miMedicoId ? '' : 'Selecciona primero el médico'} onClick={() => { setFiltroFav(filtroFavVacio); setModal('vademecum'); }}>Vademécum</Button>
      </div>
      <div>
        <span className={lbl}>Recomendaciones no farmacológicas:</span>
        <AutoTextarea className="mt-1" value={form.recomendaciones} onChange={(e) => setForm({ ...form, recomendaciones: e.target.value })} />
      </div>
      <div className="flex gap-2 border-t pt-3">
        <Button size="sm" isLoading={guardar.isPending} disabled={!miMedicoId} onClick={() => guardar.mutate()}>Guardar</Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancelar</Button>
      </div>

      {/* Lista Medicamentos: catálogo de Inventario, búsqueda en el servidor */}
      {modal === 'medicamentos' && (
        <Modal title="Medicamentos" onClose={() => setModal(null)} wide>
          <div className="space-y-2">
            <div className="relative">
              <input className={fieldCls} placeholder="Buscar por genérico, nombre comercial, concentración, código o laboratorio (ej.: amoxicilina 500)" autoFocus value={busqMed} onChange={(e) => setBusqMed(e.target.value)} />
              {buscando && <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
            </div>
            <div className="max-h-[55vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted"><tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2 px-3">Genérico (DCI)</th><th className="py-2 px-3">Nombre comercial</th>
                  <th className="py-2 px-3">Concentración</th><th className="py-2 px-3">Presentación</th><th className="py-2 px-3 text-right">Stock</th>
                </tr></thead>
                <tbody>
                  {(buscados?.items ?? []).map((m) => (
                    <tr key={m.id} className="border-t cursor-pointer hover:bg-primary/5" onClick={() => elegirMed(m)}>
                      <td className="py-1.5 px-3 font-medium">{m.principio_activo ?? '—'}</td>
                      <td className="py-1.5 px-3">{m.nombre}{m.requiere_receta && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800" title="Venta bajo receta">Rx</span>}
                        {m.marca && <div className="text-[11px] text-muted-foreground">{m.marca}</div>}</td>
                      <td className="py-1.5 px-3">{m.concentracion ?? '—'}</td>
                      <td className="py-1.5 px-3">{m.presentacion ?? '—'}{m.forma && <div className="text-[11px] text-muted-foreground">{m.forma}{m.via ? ` · vía ${m.via.toLowerCase()}` : ''}</div>}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{Number(m.stock) > 0 ? Number(m.stock) : <span className="text-muted-foreground">—</span>}</td>
                    </tr>
                  ))}
                  {!buscando && (buscados?.items ?? []).length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">
                      {busqDeb ? `Sin medicamentos para «${busqDeb}». Puede escribirlo a mano en la fila de la receta.` : 'El catálogo de medicamentos de la empresa está vacío: se cargan en Inventario → Productos (tipo Medicamento).'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{buscados ? (buscados.total > buscados.items.length ? `Mostrando ${buscados.items.length} de ${buscados.total}: afine la búsqueda` : `${buscados.total} medicamento(s)`) : ''}</span>
              <Button size="sm" variant="outline" onClick={() => setModal(null)}>Cerrar</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Vademécum (favoritos del médico, más usados primero) con filtro por columna */}
      {modal === 'vademecum' && (() => {
        const norm = (t: string | null | undefined) => (t ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const cols: { k: keyof typeof filtroFavVacio; label: string }[] = [
          { k: 'medicamento', label: 'Genérico' }, { k: 'concentracion', label: 'Concentración' },
          { k: 'forma', label: 'Presentación' }, { k: 'indicaciones', label: 'Indicaciones' },
        ];
        const filtrados = favoritos.filter((f) => cols.every((c) => !filtroFav[c.k].trim() || norm(f[c.k]).includes(norm(filtroFav[c.k].trim()))));
        const hayFiltro = cols.some((c) => filtroFav[c.k].trim());
        return (
          <Modal title="Vademécum" onClose={() => setModal(null)} wide>
            <div className="space-y-2">
              <div className="max-h-[55vh] overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-2 px-3 w-10">N°</th>
                      {cols.map((c) => <th key={c.k} className="py-2 px-3">{c.label}</th>)}
                    </tr>
                    <tr className="bg-muted">
                      <th />
                      {cols.map((c, i) => (
                        <th key={c.k} className="px-2 pb-2">
                          <input className={`${fieldCls} h-8 text-xs font-normal`} placeholder={`Filtrar ${c.label.toLowerCase()}…`} autoFocus={i === 0}
                            value={filtroFav[c.k]} onChange={(e) => setFiltroFav({ ...filtroFav, [c.k]: e.target.value })} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {favoritos.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">El médico aún no tiene medicamentos favoritos</td></tr>}
                    {favoritos.length > 0 && filtrados.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Ningún favorito coincide con los filtros</td></tr>}
                    {filtrados.map((f, i) => (
                      <tr key={f.id} className="border-t cursor-pointer hover:bg-primary/5" onClick={() => elegirMed(f)}>
                        <td className="py-1.5 px-3 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 px-3 font-medium">{f.medicamento}</td>
                        <td className="py-1.5 px-3">{f.concentracion ?? '—'}</td>
                        <td className="py-1.5 px-3">{f.forma ?? '—'}</td>
                        <td className="py-1.5 px-3">{f.indicaciones ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{hayFiltro ? `${filtrados.length} de ${favoritos.length} favorito(s)` : `${favoritos.length} favorito(s)`}</span>
                <div className="flex gap-2">
                  {hayFiltro && <Button size="sm" variant="ghost" onClick={() => setFiltroFav(filtroFavVacio)}>Limpiar filtros</Button>}
                  <Button size="sm" variant="outline" onClick={() => setModal(null)}>Cerrar</Button>
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
